import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { CatalogCustomer } from "@/shared/models/CatalogCustomer";
import type { CatalogSection } from "@/shared/models/CatalogSection";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import { getCatalogCustomers } from "@/shared/repositories/CatalogCustomerRepository";
import { getCatalogSections } from "@/shared/repositories/CatalogSectionRepository";
import {
	FILTERS_STORAGE_KEY,
	type WorkOrderFiltersState,
	loadStoredFilters,
} from "../components/WorkOrderFilters";

const initialStored = loadStoredFilters();

const EMPTY_FILTERS: WorkOrderFiltersState = {
	customer: null,
	section: null,
	gradeName: null,
	statuses: [],
};

export const useWorkOrderFilters = (workOrders: WorkOrder[] | undefined) => {
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<WorkOrderFiltersState>({
		...EMPTY_FILTERS,
		statuses: initialStored?.statuses ?? [],
	});
	const [filtersVersion, setFiltersVersion] = useState(0);

	const { data: customers } = useQuery<CatalogCustomer[], Error>({
		queryKey: ["catalogCustomers"],
		queryFn: getCatalogCustomers,
	});

	const { data: sections } = useQuery<CatalogSection[], Error>({
		queryKey: ["catalogSections"],
		queryFn: getCatalogSections,
	});

	// Restore customer/section from localStorage once query data loads
	const restoredRef = useRef({ customer: false, section: false });

	useEffect(() => {
		if (restoredRef.current.customer || !initialStored?.customerId) return;
		const match = (customers || []).find(
			(c) => c.CustomerId === initialStored.customerId,
		);
		if (match) {
			restoredRef.current.customer = true;
			setFilters((prev) => ({ ...prev, customer: match }));
		}
	}, [customers]);

	useEffect(() => {
		if (restoredRef.current.section || !initialStored?.sectionId) return;
		const match = (sections || []).find(
			(s) => s.SectionId === initialStored.sectionId,
		);
		if (match) {
			restoredRef.current.section = true;
			setFilters((prev) => ({ ...prev, section: match }));
		}
	}, [sections]);

	const clearFilters = useCallback(() => {
		restoredRef.current = { customer: true, section: true };
		try {
			localStorage.removeItem(FILTERS_STORAGE_KEY);
		} catch {
			// ignore storage write failures
		}
		setFilters(EMPTY_FILTERS);
		setFiltersVersion((v) => v + 1);
	}, []);

	// Count active filters for badge
	const activeFilterCount = useMemo(() => {
		let count = 0;
		if (filters.customer) count++;
		if (filters.section) count++;
		if (filters.gradeName) count++;
		count += filters.statuses.length;
		return count;
	}, [filters.customer, filters.section, filters.gradeName, filters.statuses]);

	// Client-side filtering (customer/section/status)
	const filteredRows = useMemo(() => {
		let rows = workOrders || [];
		if (filters.customer)
			rows = rows.filter(
				(wo) => wo.CustomerName === filters.customer!.CustomerName,
			);
		if (filters.section)
			rows = rows.filter(
				(wo) => wo.SectionName === filters.section!.SectionName,
			);
		if (filters.gradeName)
			rows = rows.filter((wo) => wo.GradeName === filters.gradeName);
		if (filters.statuses.length > 0)
			rows = rows.filter((wo) => filters.statuses.includes(wo.Status));
		return rows;
	}, [workOrders, filters]);

	return {
		filters,
		setFilters,
		clearFilters,
		filtersVersion,
		filteredRows,
		activeFilterCount,
		showFilters,
		setShowFilters,
		customers: customers || [],
		sections: sections || [],
	};
};
